/**
 * AI 业务栏目首页（/solutions?category=ai-business）的内容数据。
 *
 * 文案来源：docs/ai-data-security-solution.html（客户确认的设计稿）。
 * 一期以常量承载（与视图分离），二期如需后台可编辑，可整体迁移为
 * /api/front/* 数据源，组件按本文件的数据结构渲染即可。
 */

/** 富文本段落：一段内的普通/加粗片段序列 */
export interface RichSegment {
  text: string;
  strong?: boolean;
}

// ─── 1. Hero ─────────────────────────────────────────────────────────────────

export interface AiHeroBadge {
  icon: string;
  text: string;
  solid?: boolean;
}

export interface AiHeroStat {
  num: string;
  unit: string;
  label: string;
}

export interface AiHero {
  badges: AiHeroBadge[];
  /** 标题两行：第一行含渐变强调片段 */
  titleLine1Pre: string;
  titleLine1Highlight: string;
  titleLine2: string;
  lead: RichSegment[];
  stats: AiHeroStat[];
}

export const AI_HERO: AiHero = {
  badges: [
    { icon: "ri-shield-check-line", text: "AI 数据安全一体化解决方案", solid: true },
    { icon: "ri-robot-2-line", text: "私有化部署 · 数据不出网" },
    { icon: "ri-git-merge-line", text: "流程级及以上深度一体化" },
  ],
  titleLine1Pre: "让企业",
  titleLine1Highlight: "安全地用好 AI",
  titleLine2: "从模型到数据，全链路可控可审",
  lead: [
    { text: "AI 正在进入企业的每一个业务场景，但数据外流、模型越狱、智能体越权、报告外发失控也随之而来。我们把 AI 场景下的" },
    { text: "数据合规、模型防护、智能体治理、外发管控", strong: true },
    { text: "收敛为一套私有化、数据不出网、全程可审计的一体化解决方案。" },
  ],
  stats: [
    { num: "4", unit: "道", label: "AI 安全关口，全程闭环" },
    { num: "151", unit: "+", label: "安全设备适配器，统一纳管" },
    { num: "100", unit: "%", label: "私有化部署，推理不出内网" },
    { num: "1", unit: "人", label: "驻场工程师，贴身共建" },
  ],
};

/** Hero 右侧「四道关口」预览卡 */
export interface AiGatePreviewItem {
  icon: string;
  name: string;
  desc: string;
  level: string;
}

export const AI_GATE_PREVIEW: {
  title: string;
  liveLabel: string;
  items: AiGatePreviewItem[];
  foot: RichSegment[];
} = {
  title: "AI 安全四道关口",
  liveLabel: "全程审计中",
  items: [
    { icon: "ri-code-s-slash-line", name: "代码入口关", desc: "AI 应用与技能上线前安全门禁", level: "流程级" },
    { icon: "ri-shield-flash-line", name: "模型调用关", desc: "对话内容实时检测，风险运行中拦截", level: "流程级" },
    { icon: "ri-robot-2-line", name: "智能体行为关", desc: "AI 资产一本账，行为一键归因", level: "数据级" },
    { icon: "ri-file-shield-2-line", name: "数据外发关", desc: "外发走审核通道，水印可溯源", level: "流程级" },
  ],
  foot: [
    { text: "四道关口的检测记录汇入" },
    { text: "同一条审计时间线", strong: true },
    { text: "，与运营动作同屏呈现、同链追溯。" },
  ],
};

// ─── 2. 痛点 ─────────────────────────────────────────────────────────────────

export interface AiPainPoint {
  icon: string;
  title: string;
  desc: string;
}

export const AI_PAIN: {
  eyebrow: string;
  title: string;
  sub: string;
  items: AiPainPoint[];
} = {
  eyebrow: "Why · 为什么是现在",
  title: "AI 让数据安全的边界彻底变了",
  sub: "传统边界防护假设「数据在系统里、人在系统外」。而 AI 场景下，数据会随对话流出、随模型推理流转、随智能体调用扩散——原有手段在西侧依然有效，在 AI 侧却大面积失效。",
  items: [
    {
      icon: "ri-chat-private-line",
      title: "数据随对话流出",
      desc: "员工把合同、图纸、财报粘进大模型对话框，敏感信息在毫秒之间离开内网，传统 DLP 对浏览器内文本粘贴几乎无感。",
    },
    {
      icon: "ri-bug-2-line",
      title: "提示词攻击与模型越狱",
      desc: "注入、越狱、多编码绕过等攻击可让模型突破预设边界，输出违规内容或执行越权指令，且攻击样本随对话即时消失、难以留证。",
    },
    {
      icon: "ri-robot-3-line",
      title: "智能体成了新的攻击面",
      desc: "终端上悄悄跑的 Agent、加载的 Skill、接进来的 MCP，谁是合规的、谁在越权外传，缺乏统一台账与归因能力。",
    },
    {
      icon: "ri-cloud-off-line",
      title: "上云即触碰合规红线",
      desc: "政务、央国企、金融等场景要求数据不出网，而多数 AI 安全能力是云端 SaaS——「想用 AI 又不敢用 AI」的现实困境。",
    },
  ],
};

// ─── 3. 方案定位 ─────────────────────────────────────────────────────────────

export interface AiPositioningCard {
  badge: string;
  title: string;
  desc: string;
}

export const AI_POSITIONING: {
  eyebrow: string;
  title: string;
  sub: string;
  cards: AiPositioningCard[];
  note: RichSegment[];
} = {
  eyebrow: "Positioning · 方案定位",
  title: "不是工具堆叠，而是三级递进的深度一体化",
  sub: "AI 安全产品与业务系统的结合，业界普遍停留在「工具调用」层面——能查、能用，但不改变任何流程。本方案的做法是把融合深度逐级推高：从「能调用」到「受控」，再到「一本账」。每一级都对应你能感知、能验收的价值。",
  cards: [
    {
      badge: "L1",
      title: "接入级 · 能力可用可查",
      desc: "安全能力作为工具被调用，结果可查。这是业界通行的做法，解决了「接口通不通」的问题，但安全制度依然停留在纸面，依赖人的自觉执行。",
    },
    {
      badge: "L2",
      title: "流程级 · 制度变成机制",
      desc: "安全能力参与关键业务流程：不通过检查不放行、风险事件驱动策略、外发走指定通道。安全制度从「纸面规定」变成自动生效的机制，不再依赖事后人工抽查。",
    },
    {
      badge: "L3",
      title: "数据级 · 一本账看清全局",
      desc: "会话、技能、资产、告警共用同一本台账，安全视角贯穿每个界面。每个动作可归因、可追溯，不需要在多个后台之间对账，也不用维护两套数据。",
    },
  ],
  note: [
    { text: "本方案的各项能力" },
    { text: "均达到流程级（L2）及以上", strong: true },
    { text: "，其中行为治理与资产治理达到" },
    { text: "数据级（L3）", strong: true },
    { text: "，并逐项提供可现场演示的验收标准。同时保持架构开放：第三方设备与工具走标准协议接入，审计数据支持导出——" },
    { text: "架构开放，进退自主", strong: true },
    { text: "，你的数据资产始终属于你。" },
  ],
};

// ─── 4. 方案架构 ─────────────────────────────────────────────────────────────

export interface AiArchCell {
  title: string;
  desc: string;
  /** accent：品牌蓝浅底强调；core：深底核心格 */
  tone?: "accent" | "core";
}

export interface AiArchLayer {
  key: string;
  label: string;
  cells: AiArchCell[];
}

export const AI_ARCHITECTURE: {
  eyebrow: string;
  title: string;
  sub: string;
  barLeft: string;
  barRight: string;
  layers: AiArchLayer[];
} = {
  eyebrow: "Architecture · 方案架构",
  title: "五层架构，把 AI 安全贯穿业务全链路",
  sub: "整套方案部署在你的本地内网。从数据进入、模型调用、智能体执行到结果外发，每一层都有对应的安全能力，所有记录汇入同一条审计时间线。",
  barLeft: "你的本地内网 · 数据不出网",
  barRight: "统一审计时间线 · 单一台账 · 可导出",
  layers: [
    {
      key: "L5",
      label: "服务与运营",
      cells: [
        { title: "驻场工程师", desc: "基于你的实际场景定制 AI 安全策略与编排", tone: "accent" },
        { title: "安全专家智能体", desc: "研判 / 溯源 / 处置，即需即唤", tone: "accent" },
        { title: "持续运营", desc: "时间线月报，定期呈报安全管理方", tone: "accent" },
      ],
    },
    {
      key: "L4",
      label: "数据与治理",
      cells: [
        { title: "敏感数据分类分级", desc: "行业方案库 + AI 识别自动打标" },
        { title: "AI 资产一本账", desc: "终端 Agent / Skill / MCP 全量登记" },
        { title: "审计台账", desc: "会话、技能、资产、告警同源存储" },
      ],
    },
    {
      key: "L3",
      label: "安全关口",
      cells: [
        { title: "入口 · 代码安全审计", desc: "AI 应用与技能发布即过检", tone: "accent" },
        { title: "运行 · 大模型安全围栏", desc: "内容实时检测与拦截", tone: "accent" },
        { title: "行为 · AI 资产治理", desc: "越权与恶意 Agent 识别", tone: "accent" },
        { title: "出口 · 终端 DLP", desc: "外发审核通道 + 水印固证", tone: "accent" },
      ],
    },
    {
      key: "L2",
      label: "运行时底座",
      cells: [
        { title: "隔离沙箱", desc: "危险操作在隔离环境执行，可审计、可回退", tone: "core" },
        { title: "统一接口网关", desc: "各家安全设备 API 统一接入、统一鉴权", tone: "core" },
        { title: "本地模型服务", desc: "自建 / 自购模型，推理不出内网", tone: "core" },
      ],
    },
    {
      key: "L1",
      label: "基础设施",
      cells: [
        { title: "算力资源", desc: "GPU / 推理服务器，可按需扩容" },
        { title: "网络隔离", desc: "管理网与业务网分离，按区分权" },
        { title: "既有安全设备", desc: "防火墙 / WAF / EDR / 日志审计全部利旧接入" },
      ],
    },
  ],
};

// ─── 5. 四道关口 ─────────────────────────────────────────────────────────────

export interface AiGateCard {
  no: string;
  name: string;
  phase: string;
  desc: string;
  verify: string;
}

export const AI_GATES: {
  eyebrow: string;
  title: string;
  sub: string;
  cards: AiGateCard[];
} = {
  eyebrow: "Capability · 四道关口",
  title: "四道关口，让 AI 的每一步都在授权可控范围内",
  sub: "四道关口分别守住 AI 应用的生命周期关键位置。每道关口均达到流程级及以上融合深度，并配有可现场演示的验收标准——不是能力清单，而是可验证的机制。",
  cards: [
    {
      no: "GATE 01",
      name: "代码入口关",
      phase: "AI 应用与技能 · 发布即过检",
      desc: "智能静态应用安全测试能力，覆盖安全漏洞、合规标准、敏感信息与 IaC。AI 应用、技能、插件在编写与发布环节自动扫描，不通过则发布不生效。",
      verify: "发布一个含硬编码密钥的技能，系统自动阻塞并留痕；修复后放行，体检报告按版本可查。",
    },
    {
      no: "GATE 02",
      name: "模型调用关",
      phase: "大模型安全围栏 · 运行中拦截",
      desc: "提示词攻击防护（注入 / 越狱 / 多编码）、内容无害化、敏感信息识别脱敏。流式检测边生成边检查，高风险内容实时终止并留痕。",
      verify: "构造注入攻击样本对话，会话在运行中被中断、时间线留痕、可复盘归因；高敏感操作自动升级审批。",
    },
    {
      no: "GATE 03",
      name: "智能体行为关",
      phase: "AI 资产治理 · 一本账归因",
      desc: "持续清查终端上的 Agent / Skill / MCP / 定时任务 / 模型入口，形成 AI 资产账与能力关系图，识别供应链污染、数据外传、权限越界等风险。",
      verify: "在终端装载一个未授权技能，系统归因到具体会话与操作者，并可走限制 / 隔离 / 回收处置闭环。",
    },
    {
      no: "GATE 04",
      name: "数据外发关",
      phase: "终端 DLP · 外发必留痕",
      desc: "敏感数据分类分级，外发全渠道管控（IM / 网盘 / 邮件 / 移动存储 / 打印 / 蓝牙），五级响应：审计 / 告警 / 问询 / 阻断 / 审批。",
      verify: "未审批导出含敏感数据的报告被自动阻断；审批后带水印外发且可溯源到人。",
    },
  ],
};

// ─── 6. 能力矩阵 ─────────────────────────────────────────────────────────────

export interface AiMatrixRow {
  need: string;
  capability: string;
  gate: string;
  level: "流程级" | "数据级";
  value: string;
}

export const AI_MATRIX: {
  eyebrow: string;
  title: string;
  sub: string;
  columns: string[];
  rows: AiMatrixRow[];
} = {
  eyebrow: "Matrix · 能力矩阵",
  title: "需求 — 能力 — 关口 — 价值，一张表看清",
  sub: "不同规模与行业的客户关注的 AI 安全重点不同。下表按典型需求维度梳理方案对应的能力项、所属关口与可感知价值，便于你快速定位自身短板。",
  columns: ["AI 安全需求", "对应能力", "所属关口", "融合深度", "你可感知的价值"],
  rows: [
    { need: "AI 应用与技能带病上线", capability: "代码安全审计 · 发布门禁 · 编写即扫描", gate: "入口关", level: "流程级", value: "技能市场成为「自带门禁的市场」，带病技能上不了架" },
    { need: "对话中的敏感信息外泄", capability: "大模型安全围栏 · 敏感信息识别脱敏", gate: "运行关", level: "流程级", value: "敏感内容在对话中被识别并脱敏，风险操作自动升级审批" },
    { need: "提示词注入 / 越狱攻击", capability: "提示词攻击防护 · 流式实时检测", gate: "运行关", level: "流程级", value: "攻击在运行过程中被实时拦下并留痕，可复盘、可追责" },
    { need: "终端 AI 资产底数不清", capability: "AI 资产账 · 能力关系图 · 持续清查", gate: "行为关", level: "数据级", value: "「哪台终端在跑什么 Agent」一键可查，资产持续沉淀在本地" },
    { need: "智能体越权与外传行为", capability: "行为一键归因 · 六类风险识别", gate: "行为关", level: "数据级", value: "风险归因精确到「谁、在哪个会话、用了哪个技能」" },
    { need: "敏感数据多渠道外发", capability: "分类分级 · 全渠道管控 · 五级响应", gate: "出口关", level: "流程级", value: "未经审批的外发被阻断，合规通道成为最短路径" },
    { need: "AI 相关报告泄露无法追溯", capability: "自动水印 · 外发留痕 · 流转溯源", gate: "出口关", level: "流程级", value: "即使发生外泄，也可定位、可追溯、可追责" },
    { need: "危险 AI 操作误伤生产", capability: "隔离沙箱 · 危险操作拦截", gate: "运行底座", level: "流程级", value: "高危指令在隔离环境执行，可审计、可回退，对生产零侵入" },
    { need: "安全设备接口异构、单据分散", capability: "统一接口网关 · 标准化工具注册", gate: "运行底座", level: "数据级", value: "多后台收敛为一站式入口，接口资产统一纳管" },
    { need: "数据与模型不能出内网", capability: "私有化部署 · 本地模型服务", gate: "基础设施", level: "数据级", value: "推理与数据全程留在内网，满足等保与保密合规要求" },
  ],
};

// ─── 7. 客户价值 ─────────────────────────────────────────────────────────────

export interface AiValueCard {
  icon: string;
  title: string;
  desc: string;
  unique?: boolean;
}

export const AI_VALUES: {
  eyebrow: string;
  title: string;
  sub: string;
  items: AiValueCard[];
} = {
  eyebrow: "Value · 客户价值",
  title: "你能得到什么",
  sub: "方案的价值不止于「挡住了什么」，更在于让 AI 从「不敢用」变成「放心用、规模化用」。以下八项价值中，后四项由深度一体化带来，难以通过多产品拼装复现。",
  items: [
    { icon: "ri-cloud-off-line", title: "数据不出网", desc: "私有化部署 + 本地模型 + 终端双重防泄漏，数据与推理全程留在内网。" },
    { icon: "ri-user-star-line", title: "专家开箱即用", desc: "经实战检验的安全专家智能体随方案交付，研判结论与平台记录同一本台账。" },
    { icon: "ri-speed-up-line", title: "运营提效", desc: "AI 驱动的自动化接管日常安全运营，人从重复劳动中解放，聚焦研判决策。" },
    { icon: "ri-plug-line", title: "接口统一", desc: "统一网关收口全部安全设备 API，无 API 系统走浏览器通路，异构设备统一纳管。" },
    { icon: "ri-door-open-line", title: "自带门禁的技能市场", desc: "编写即扫描、发布即过检、报告随版本——带病技能上不了架。", unique: true },
    { icon: "ri-shield-flash-line", title: "运行全程有围栏", desc: "每步 AI 操作的安全判定就在时间线上，风险内容运行中被拦下，审批随风险自动调整。", unique: true },
    { icon: "ri-robot-2-line", title: "AI 资产一本账", desc: "终端上跑的每个 Agent、加载的每个 Skill，一键归因到会话与人，与安全告警同图研判。", unique: true },
    { icon: "ri-fingerprint-line", title: "外发必留痕", desc: "AI 报告产物默认走审核通道、自动水印、可溯源到人——合规通道是最短路径。", unique: true },
  ],
};

// ─── 8. 交付路线 ─────────────────────────────────────────────────────────────

export interface AiRoadmapItem {
  bold: string;
  text: string;
}

export interface AiRoadmapPhase {
  no: string;
  level: string;
  title: string;
  items: AiRoadmapItem[];
}

export const AI_ROADMAP: {
  eyebrow: string;
  title: string;
  sub: string;
  phases: AiRoadmapPhase[];
} = {
  eyebrow: "Roadmap · 交付路线",
  title: "分期落地，每期都能现场验收",
  sub: "不追求一步到位，而是按「制度变机制 → 一本账成型 → 能力输出」的节奏推进。每一期都有可现场演示、可逐项写进验收标准的动作，效果看得见、投入可预期。",
  phases: [
    {
      no: "PHASE 01",
      level: "流程级",
      title: "制度变机制",
      items: [
        { bold: "代码入口关：", text: "发布门禁 + 编写即扫描上线，带病技能无法上架" },
        { bold: "模型调用关：", text: "操作审计上时间线，风险内容运行中拦截" },
        { bold: "数据外发关：", text: "报告外发走审核通道，水印随行" },
      ],
    },
    {
      no: "PHASE 02",
      level: "数据级",
      title: "一本账成型",
      items: [
        { bold: "智能体行为关：", text: "AI 资产一本账 + 行为一键归因 + 风险同屏研判" },
        { bold: "统一审计时间线成型：", text: "四道关口的检测记录全部汇入同一台账" },
        { bold: "与既有安全体系打通：", text: "告警、工单、企业微信消息联动" },
      ],
    },
    {
      no: "PHASE 03",
      level: "持续运营",
      title: "能力输出",
      items: [
        { bold: "审计时间线月报：", text: "定期呈报安全管理部门，形成管理闭环" },
        { bold: "一体化能力受控开放：", text: "向既有 SOC / 态势感知平台输出" },
        { bold: "场景化能力沉淀：", text: "驻场工程师持续共建，能力越用越深" },
      ],
    },
  ],
};

// ─── 9. 服务保障 ─────────────────────────────────────────────────────────────

export interface AiServiceItem {
  icon: string;
  title: string;
  desc: string;
}

export const AI_SERVICE: {
  eyebrow: string;
  title: string;
  sub: string;
  items: AiServiceItem[];
} = {
  eyebrow: "Service · 服务保障",
  title: "不只是交付一套系统，而是共建一种能力",
  sub: "AI 安全不是一次性采购能解决的问题。我们提供「规划 + 实施 + 驻场 + 运营」的整体交付，让安全能力随你的 AI 场景一起生长。",
  items: [
    { icon: "ri-team-line", title: "1 人驻场共建", desc: "工程师驻场，基于你的实际 AI 场景定制智能体与安全策略，把「通用方案」落地为「你专属的能力」。" },
    { icon: "ri-server-line", title: "私有化部署", desc: "管理端与控制台全部部署在你的内网，容器化一键部署；现场完成安全设备 API 接入与注册。" },
    { icon: "ri-customer-service-2-line", title: "安全服务配套", desc: "安全事件自动建单、处置结果自动回写、关键节点即时推送，支持远程运营管理与 7×24 响应。" },
    { icon: "ri-graduation-cap-line", title: "团队赋能培训", desc: "面向你的安全团队开展 AI 安全专项培训，覆盖风险识别、平台操作与应急处置，能力留在自己手里。" },
    { icon: "ri-file-list-3-line", title: "合规支撑", desc: "对接等保 2.0 与行业监管要求，提供 AI 场景合规差距分析与整改建议，配套审计留痕材料。" },
    { icon: "ri-refresh-line", title: "持续演进", desc: "AI 攻击手法与环境持续变化，方案按季度迭代规则与检测能力，保证防护有效性不随时间衰减。" },
  ],
};

// ─── 10. CTA + 免责声明 ──────────────────────────────────────────────────────

export const AI_CTA: {
  title: string;
  desc: string;
  primaryAction: { icon: string; text: string; href: string };
  secondaryAction: { icon: string; text: string; href: string };
  note: string;
} = {
  title: "先做一次 AI 安全现状评估，再谈方案",
  desc: "我们会基于你当前的 AI 应用清单、数据资产分布与既有安全体系，输出一份 AI 数据安全差距分析，明确优先级与落地路径——首次评估免费。",
  primaryAction: { icon: "ri-calendar-check-line", text: "预约免费评估", href: "/#contact-form" },
  secondaryAction: { icon: "ri-links-line", text: "返回解决方案总览", href: "/solutions" },
  note: "首次评估免费 · 方案细节与交付节奏可按你的环境定制",
};

export const AI_DISCLAIMER: RichSegment[] = [
  { text: "说明：", strong: true },
  { text: "本页面为 AI 数据安全解决方案的能力说明页，页面中涉及的产品能力、技术指标与验收场景均为方案通用表述，具体功能范围、性能参数与交付内容以双方正式签署的技术方案与商务合同为准。图中架构为逻辑架构示意，实际部署拓扑依客户环境确定。" },
];
